import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useInView from '../../hooks/useInView';

const STEPS = [
  {
    number: '01',
    icon: 'ri-discuss-line',
    title: 'Understand Your Business',
    summary: 'We start with a conversation — not a pitch.',
    expanded: 'Before we recommend anything, we take time to understand your business properly. That means understanding your product or service, your target customers, your competitive landscape, and where you currently stand online. We ask the questions that most agencies skip — because the quality of our strategy depends entirely on the quality of our understanding.',
    points: [
      'Discovery call to understand your goals and current situation',
      'Review of your existing digital presence and channels',
      'Competitor and market landscape review',
      'Identifying gaps and immediate opportunities',
    ],
    accent: 'text-brand-blue',
    iconBg: 'bg-brand-blue/10 border-brand-blue/25',
    numBg: 'bg-brand-blue/10 border-brand-blue/20',
    numText: 'text-brand-blue',
  },
  {
    number: '02',
    icon: 'ri-map-2-line',
    title: 'Build a Focused Strategy',
    summary: 'A clear plan — not a vague proposal.',
    expanded: 'Based on what we learn, we put together a strategy that makes sense for your specific situation. We decide which channels to prioritize, what outcomes to aim for, and what a realistic timeline looks like. We keep it clear and practical — nothing in the plan that we can\'t explain or justify in plain terms.',
    points: [
      'Channel selection based on where your audience is',
      'Clear objectives with realistic, measurable targets',
      'Timeline and phased execution plan',
      'Budget allocation recommendation',
    ],
    accent: 'text-violet-500',
    iconBg: 'bg-violet-50 border-violet-200',
    numBg: 'bg-violet-50 border-violet-200',
    numText: 'text-violet-500',
  },
  {
    number: '03',
    icon: 'ri-play-circle-line',
    title: 'Execute with Precision',
    summary: 'We put the strategy into action — and stay hands-on.',
    expanded: 'Good execution is where most agencies lose clients. We stay hands-on during the implementation phase — launching campaigns, producing content, managing platforms, and making sure every element is aligned with the strategy. You\'re kept informed throughout, not left wondering what\'s happening.',
    points: [
      'Campaign setup, creative production, and launch',
      'Content creation and scheduling',
      'Platform management and monitoring',
      'Regular check-ins during active execution phases',
    ],
    accent: 'text-orange-500',
    iconBg: 'bg-orange-50 border-orange-200',
    numBg: 'bg-orange-50 border-orange-200',
    numText: 'text-orange-500',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Track, Report & Improve',
    summary: 'Transparent reporting and continuous refinement.',
    expanded: 'We monitor performance consistently, share clear reports that explain what\'s working and what isn\'t, and adjust the approach based on what the data tells us. We don\'t hide behind metrics that don\'t matter — we focus on the numbers that are relevant to your actual business goals.',
    points: [
      'Regular performance reports (weekly or monthly)',
      'Honest assessment of what\'s working and what isn\'t',
      'Campaign and content refinements based on data',
      'Ongoing communication and strategic review calls',
    ],
    accent: 'text-brand-blue-light',
    iconBg: 'bg-blue-50 border-blue-200',
    numBg: 'bg-blue-50 border-blue-200',
    numText: 'text-brand-blue-light',
  },
];

const EXPECTATIONS = [
  {
    icon: 'ri-chat-check-line',
    title: 'Open, Regular Communication',
    desc: 'You\'re not passed off to a junior team member after onboarding. We stay in direct communication throughout, and you can always reach someone who knows your account.',
    color: 'text-brand-blue',
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-file-chart-line',
    title: 'Transparent Reporting',
    desc: 'We share reports that are easy to understand — not dashboard screenshots full of vanity metrics. You\'ll always know what\'s happening and why.',
    color: 'text-violet-500',
    bg: 'bg-violet-50 border-violet-100',
  },
  {
    icon: 'ri-time-line',
    title: 'Realistic Timelines',
    desc: 'We don\'t promise results that take months in the first week. We set honest expectations from the start — and hold ourselves to them.',
    color: 'text-orange-500',
    bg: 'bg-orange-50 border-orange-100',
  },
];

function ProcessHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);
  return (
    <section className="relative bg-white pt-32 pb-20 overflow-hidden border-b border-gray-100">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white pointer-events-none"></div>
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6">
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 100ms, transform 0.7s ease 100ms' }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand border border-brand-blue/30 bg-brand-blue/10 px-4 py-1.5 rounded-full mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse"></span>
            How We Work
          </span>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight max-w-3xl mb-6">
            Our Process
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl leading-relaxed">
            A structured, four-step approach to working with clients — built around clarity, collaboration, and continuous improvement.
          </p>
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, idx }: { step: typeof STEPS[0]; idx: number }) {
  const { ref, visible } = useInView(0.1);
  return (
    <div
      ref={ref}
      className="bg-white border border-gray-100 hover:border-gray-200 rounded-2xl p-8 md:p-10 transition-all duration-300 shadow-sm"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: `opacity 0.6s ease ${idx * 80}ms, transform 0.6s ease ${idx * 80}ms` }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${step.iconBg}`}>
              <i className={`${step.icon} text-xl ${step.accent}`}></i>
            </div>
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border px-3 py-1.5 rounded-full ${step.numBg} ${step.numText}`}>
              Step {step.number}
            </span>
          </div>
          <h3 className="font-display font-bold text-gray-900 text-xl md:text-2xl mb-2 leading-tight">{step.title}</h3>
          <p className={`text-sm font-medium ${step.accent}`}>{step.summary}</p>
        </div>
        <div className="lg:col-span-2">
          <p className="text-gray-600 text-[14px] leading-relaxed mb-6">{step.expanded}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {step.points.map(point => (
              <div key={point} className="flex items-start gap-2.5 text-gray-600 text-[13px]">
                <i className={`ri-check-line ${step.accent} mt-0.5 flex-shrink-0`}></i>
                {point}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepsSection() {
  return (
    <section className="py-20 md:py-28 bg-gray-50 border-t border-gray-100">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div className="space-y-6">
          {STEPS.map((step, i) => (
            <StepCard key={step.number} step={step} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ExpectationCard({ e, delay }: { e: typeof EXPECTATIONS[0]; delay: number }) {
  const { ref, visible } = useInView(0.1);
  return (
    <div ref={ref} className="bg-white border border-gray-100 rounded-2xl p-7" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms` }}>
      <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${e.bg} mb-5`}><i className={`${e.icon} text-xl ${e.color}`}></i></div>
      <h3 className="font-display font-semibold text-gray-900 text-[15px] mb-2.5 leading-snug">{e.title}</h3>
      <p className="text-gray-500 text-[13px] leading-relaxed">{e.desc}</p>
    </div>
  );
}

function WhatToExpect() {
  const { ref: headRef, visible: headVisible } = useInView(0.2);
  return (
    <section className="py-20 md:py-24 bg-gray-50 border-t border-b border-gray-100">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={headRef}
          className="mb-12"
          style={{ opacity: headVisible ? 1 : 0, transform: headVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-5">Working With Us</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 leading-tight">
            What You Can Expect
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {EXPECTATIONS.map((e, i) => (
            <ExpectationCard key={e.title} e={e} delay={i * 80} />
          ))}
        </div>
        <div className="mt-8 bg-white border border-gray-100 rounded-2xl p-7 flex flex-col md:flex-row items-start md:items-center gap-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-blue/10 border border-brand-blue/20 flex-shrink-0">
            <i className="ri-hand-heart-line text-xl text-brand-blue"></i>
          </div>
          <div>
            <p className="text-gray-900 font-display font-semibold text-[15px] mb-1">We work with clients, not just for them</p>
            <p className="text-gray-500 text-sm leading-relaxed">
              Great digital marketing is a two-way partnership. The more we understand your business, the better we can serve it. Open communication is built into how we work — not offered as an add-on.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProcessCTA() {
  const { ref, visible } = useInView(0.2);
  return (
    <section className="py-20 bg-blue-50/50 border-t border-gray-100">
      <div ref={ref} className="max-w-site mx-auto px-4 sm:px-6 text-center" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 mb-5">Ready to Get Started?</h2>
        <p className="text-gray-600 text-[15px] max-w-md mx-auto mb-8 leading-relaxed">
          Step one is just a conversation. Let's understand your business and see how we can help.
        </p>
        <Link to="/contact" className="btn-primary text-base px-8 py-4">
          Start the Conversation
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

export default function ProcessPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ProcessHero />
      <StepsSection />
      <WhatToExpect />
      <ProcessCTA />
      <Footer />
    </div>
  );
}
